import { useState, useMemo, useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { detectLang } from "./i18n";
import { scenes } from "./data/scenes";
import TrackSelector from "./components/TrackSelector";
import FullscreenPlayer from "./components/FullscreenPlayer";
import ImmersivePlayer from "./components/ImmersivePlayer";
import PlayerBar from "./components/PlayerBar";
import TrackDetailsModal from "./components/TrackDetailsModal";
import PlaylistModal from "./components/PlaylistModal";
import UserLibraryModal from "./components/UserLibraryModal";
import PricingModal from "./components/PricingModal";
import HeroSection from "./components/HeroSection";
import EmailLoginModal from "./components/EmailLoginModal";
import LoginMethodModal from "./components/LoginMethodModal";
import GuestPromptModal from "./components/GuestPromptModal";
import Navbar from "./components/Navbar";
import SceneBrowser from "./components/SceneBrowser";
import PlaylistsSection from "./components/PlaylistsSection";
import Footer from "./components/Footer";
import AdminPage from "./pages/AdminPage";
import PlaylistsPage from "./pages/PlaylistsPage";
import PlaylistDetailPage from "./pages/PlaylistDetailPage";

import ImmersivePlayerPage from "./pages/ImmersivePlayerPage";
import ProfilePage from "./pages/ProfilePage";
import SceneEditor from "./pages/SceneEditor";
import useToast from "./hooks/useToast";
import useLibrary from "./hooks/useLibrary";
import useAuth from "./hooks/useAuth";
import usePlayer from "./hooks/usePlayer";
import { PlayerProvider } from "./providers/PlayerProvider";



export default function App() {
  const [lang, setLang] = useState(detectLang());

  // ===== Hooks =====
  const { toast, showToast } = useToast();
  const {
    likedTracks,
    favoritedTracks,
    syncLibraryFromServer,
    onToggleLike,
    onToggleFavorite,
    resetLibrary,
    syncUser,
  } = useLibrary(showToast);
  const {
    user,
    isAdmin,
    userInitials,
    logout: authLogout,
    updateAuthFromEmail,
    handleCredentialResponse,
  } = useAuth({ onLoginSuccess: (data) => syncLibraryFromServer(data.token), onCloseModals: () => { setShowLoginMethod(false); setShowEmailLogin(false); } });
  const isLoggedIn = !!user;
  const [showLoginMethod, setShowLoginMethod] = useState(false);
  const [guestPrompt, setGuestPrompt] = useState(null); // { title, description } | null
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  function handleEmailLoginSuccess(data) {
    updateAuthFromEmail(data);
  }
  syncUser(user);

  // ===== Modal state (not player-related) =====
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTag, setActiveTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [trackSelectorScene, setTrackSelectorScene] = useState(null);
  const [trackDetails, setTrackDetails] = useState(null);
  const [showUserLibrary, setShowUserLibrary] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [userLibraryTab, setUserLibraryTab] = useState("liked");
  const [showPlaylist, setShowPlaylist] = useState(false);

  // ===== Route change: auto-close modals =====
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setTrackDetails(null);
      setShowPlaylist(false);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  // ===== Payment success notification from Stripe redirect =====
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = params.get("payment");
    if (p === "success") {
      showToast("Payment successful! You now have unlimited downloads.", "success");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p === "canceled") {
      showToast("Payment canceled. You can try again anytime.", "info");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== i18n =====
  function toggleLang() {
    const next = lang === "zh" ? "en" : "zh";
    setLang(next);
    localStorage.setItem("fm_lang", next);
  }

  // ===== Logout =====
  function handleLogout() {
    authLogout();
    resetLibrary();
    setShowUserMenu(false);
  }

  // ===== Scroll helpers =====
  function scrollToExplore() {
    document.getElementById("explore")?.scrollIntoView({ behavior: "smooth" });
  }
  function scrollToPlaylists() {
    document.getElementById("playlists-section")?.scrollIntoView({ behavior: "smooth" });
  }

  // ===== Filtered scenes =====
  const filteredScenes = useMemo(() => {
    let result = scenes;
    if (activeCategory !== "all") {
      result = result.filter((s) => s.category === activeCategory);
    }
    if (activeTag) {
      result = result.filter((s) =>
        s.tags.some((t) => t.toLowerCase().includes(activeTag.toLowerCase()))
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.nameEn.toLowerCase().includes(q) ||
          s.tags.some((t) => t.toLowerCase().includes(q)) ||
          s.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, activeTag, searchQuery]);

  // ===== Tag click =====
  function handleTagClick(tag) {
    setActiveTag(tag);
  }

  // ===== User library =====
  function openUserLibrary(tab) {
    setUserLibraryTab(tab || "liked");
    setShowUserLibrary(true);
    setShowUserMenu(false);
  }

  // ===== Reset filters =====
  function resetFilters() {
    setActiveTag(null);
    setActiveCategory("all");
    setSearchQuery("");
  }

  return (
    <PlayerProvider
      user={user}
      isLoggedIn={isLoggedIn}
      likedTracks={likedTracks}
      favoritedTracks={favoritedTracks}
      showToast={showToast}
    >
      <AppInner
        // Lang
        lang={lang}
        toggleLang={toggleLang}
        // User/Auth
        user={user}
        userInitials={userInitials}
        isAdmin={isAdmin}
        isLoggedIn={isLoggedIn}
        handleLogout={handleLogout}
        handleCredentialResponse={handleCredentialResponse}
        // Toast
        toast={toast}
        // Library
        likedTracks={likedTracks}
        favoritedTracks={favoritedTracks}
        onToggleLike={onToggleLike}
        onToggleFavorite={onToggleFavorite}
        // Modals
        showLoginMethod={showLoginMethod}
        setShowLoginMethod={setShowLoginMethod}
        showEmailLogin={showEmailLogin}
        setShowEmailLogin={setShowEmailLogin}
        handleEmailLoginSuccess={handleEmailLoginSuccess}
        guestPrompt={guestPrompt}
        setGuestPrompt={setGuestPrompt}
        // Filters
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        activeTag={activeTag}
        setActiveTag={setActiveTag}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchFocused={searchFocused}
        setSearchFocused={setSearchFocused}
        filteredScenes={filteredScenes}
        // Pickers
        trackSelectorScene={trackSelectorScene}
        setTrackSelectorScene={setTrackSelectorScene}
        trackDetails={trackDetails}
        setTrackDetails={setTrackDetails}
        // Library modal
        showUserLibrary={showUserLibrary}
        setShowUserLibrary={setShowUserLibrary}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        userLibraryTab={userLibraryTab}
        setUserLibraryTab={setUserLibraryTab}
        showPricingModal={showPricingModal}
        setShowPricingModal={setShowPricingModal}
        showPlaylist={showPlaylist}
        setShowPlaylist={setShowPlaylist}
        // Actions
        handleTagClick={handleTagClick}
        openUserLibrary={openUserLibrary}
        scrollToExplore={scrollToExplore}
        scrollToPlaylists={scrollToPlaylists}
        resetFilters={resetFilters}
      />
    </PlayerProvider>
  );
}

// ──────────────────────────────────────────────
// AppInner — reads usePlayer() from Provider context
// ──────────────────────────────────────────────
function AppInner({
  lang, toggleLang,
  user, userInitials, isAdmin, handleLogout, handleCredentialResponse,
  toast,
  likedTracks, favoritedTracks, onToggleLike, onToggleFavorite,
  showLoginMethod, setShowLoginMethod, showEmailLogin, setShowEmailLogin, handleEmailLoginSuccess,
  guestPrompt, setGuestPrompt,
  activeCategory, setActiveCategory, activeTag, setActiveTag,
  searchQuery, setSearchQuery, searchFocused, setSearchFocused, filteredScenes,
  trackSelectorScene, setTrackSelectorScene,
  trackDetails, setTrackDetails,
  showUserLibrary, setShowUserLibrary, showUserMenu, setShowUserMenu,
  userLibraryTab,
  showPricingModal, setShowPricingModal,
  showPlaylist, setShowPlaylist,
  handleTagClick, openUserLibrary, scrollToExplore, scrollToPlaylists, resetFilters,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // playlists — currently empty placeholder; wire up to real data via useLibrary / usePlaylist when needed
  const playlists = [];
  const {
    audioRef,
    currentTrack,
    currentScene,
    isPlaying,
    playlist,
    exploreTracks,
    tempPlaylist,
    playlistSource,
    playlistName,
    loopMode,
    playerMode,
    setPlayerMode,
    setPlaylistSource,
    setLoopMode,
    playTrack,
    togglePlay,
    nextTrack,
    prevTrack,
    handleDislike,
    handleShare,
    removeFromPlaylist,
    clearPlaylist,
    addToTempPlaylist,
    removeFromTempPlaylist,
    clearTempPlaylist,
    handleCreatePlaylist,
  } = usePlayer();

  function openImmersive() {
    // If a track is already loaded, just switch to immersive mode
    if (currentTrack) {
      setPlayerMode("immersive");
      return;
    }
    // No track — try auto-playing first available track
    const sources = exploreTracks.length > 0 ? exploreTracks : playlist;
    if (sources.length > 0) {
      playTrack(sources[0], null);
      setTimeout(() => setPlayerMode("immersive"), 300);
    }
  }

  // Auto-play track from TrackDetailPage navigation state
  useEffect(() => {
    const state = location.state;
    if (state?.autoPlayTrack) {
      const track = state.autoPlayTrack;
      const sc = state.autoPlayScene;
      const sceneObj = sc?.id ? sc : scenes.find(s => s.category === track.category);
      playTrack(track, sceneObj || null);
      // Open immersive player if not already
      setTimeout(() => setPlayerMode("immersive"), 300);
      // Clear state so it doesn't replay on re-render
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-play track from SSR: ?play=xxx or #track=xxx (fallbacks for old links)
  useEffect(() => {
    let playId = new URLSearchParams(location.search).get("play");
    if (!playId) {
      const m = window.location.hash.match(/track=([^&]+)/);
      if (m) playId = m[1];
    }
    if (playId) {
      playTrack({ id: playId }, null);
      setTimeout(() => setPlayerMode("immersive"), 300);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for open-login-modal custom event (dispatched by download buttons when not logged in)
  useEffect(() => {
    function handler(e) {
      setGuestPrompt({
        title: e.detail?.title || null,
        description: e.detail?.description || null,
      });
    }
    window.addEventListener("open-login-modal", handler);
    return () => window.removeEventListener("open-login-modal", handler);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-20">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Helmet>
                  <title>FreeMusic.Top — Top Music, Free for Listeners and Creators</title>
                  <link rel="canonical" href="https://freemusic.top/" />
                  <meta property="og:image" content="https://freemusic.top/og-image.png" />
                  <meta property="og:image:width" content="1200" />
                  <meta property="og:image:height" content="630" />
                </Helmet>
                <Navbar
                  lang={lang}
                  user={user}
                  showUserMenu={showUserMenu}
                  setShowUserMenu={setShowUserMenu}
                  userInitials={userInitials}
                  isAdmin={isAdmin}
                  onOpenUserLibrary={openUserLibrary}
                  onLogout={handleLogout}
                  onToggleLang={toggleLang}
                  onNavigate={navigate}
                  onResetFilters={resetFilters}
                  onOpenEmailLogin={() => setShowLoginMethod(true)}
                />

                <HeroSection
                  lang={lang}
                  onScrollToPlaylists={scrollToPlaylists}
                  onOpenImmersive={openImmersive}
                />

                <SceneBrowser
                  lang={lang}
                  activeCategory={activeCategory}
                  setActiveCategory={setActiveCategory}
                  activeTag={activeTag}
                  setActiveTag={setActiveTag}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  searchFocused={searchFocused}
                  setSearchFocused={setSearchFocused}
                  filteredScenes={filteredScenes}
                  onSelectScene={(scene) => setTrackSelectorScene(scene)}
                />

                <PlaylistsSection
                  lang={lang}
                  onNavigate={navigate}
                />

                <Footer
                  lang={lang}
                  onScrollToExplore={scrollToExplore}
                  onScrollToPlaylists={scrollToPlaylists}
                />
              </>
            }
          />
          <Route
            path="/admin"
            element={<AdminPage lang={lang} />}
          />
          <Route
            path="/playlists"
            element={<PlaylistsPage lang={lang} />}
          />
          <Route
            path="/playlist/:id"
            element={<PlaylistDetailPage lang={lang} />}
          />
          <Route
            path="/profile"
            element={<ProfilePage lang={lang} />}
          />
          <Route
            path="/immersive_player"
            element={
              <ImmersivePlayerPage
                lang={lang}
                likedTracks={likedTracks}
                favoritedTracks={favoritedTracks}
                onToggleLike={onToggleLike}
                onToggleFavorite={onToggleFavorite}
                playlists={[]}
              />
            }
          />
          <Route path="/scene/create" element={<SceneEditor />} />
          <Route path="/scene/:id" element={<SceneEditor />} />
        </Routes>

        {/* === Modals === */}

        {/* Track Selector */}
        {trackSelectorScene && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm md:items-center md:justify-center md:p-4 bottom-0 items-stretch justify-end p-0"
            onClick={(e) => {
              if (e.target === e.currentTarget) setTrackSelectorScene(null);
            }}
          >
            <div className="bg-white rounded-t-3xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col w-full max-w-2xl md:max-h-[80vh] md:rounded-2xl md:relative md:bottom-auto md:shadow-2xl bottom-0 relative">
            <TrackSelector
              scene={trackSelectorScene}
              onPlayTrack={(track) => playTrack(track, trackSelectorScene)}
              onShowDetails={(track) =>
                setTrackDetails({ track, scene: trackSelectorScene })
              }
              onAddToPlaylist={addToTempPlaylist}
              onToggleLike={(track) => onToggleLike(track, trackSelectorScene)}
              onToggleFavorite={(track) => onToggleFavorite(track, trackSelectorScene)}
              likedTracks={likedTracks}
              favoritedTracks={favoritedTracks}
              onClose={() => setTrackSelectorScene(null)}
              onShowPricing={() => setShowPricingModal(true)}
              lang={lang}
            />
            </div>
          </div>
        )}

        {/* Track Details Modal */}
        {trackDetails && (
          <TrackDetailsModal
            track={trackDetails.track}
            scene={trackDetails.scene}
            lang={lang}
            likedTracks={likedTracks}
            favoritedTracks={favoritedTracks}
            onPlayTrack={(track) => {
              playTrack(track, trackDetails.scene);
              setTrackDetails(null);
            }}
            onAddToPlaylist={addToTempPlaylist}
            onToggleLike={(track) => onToggleLike(track, trackDetails.scene)}
            onToggleFavorite={(track) => onToggleFavorite(track, trackDetails.scene)}
            onClose={() => setTrackDetails(null)}
            onShowPricing={() => setShowPricingModal(true)}
          />
        )}

        {/* Playlist Modal */}
        {showPlaylist &&
          (() => {
            const sourceTracks =
              playlistSource === "liked"
                ? Array.from(likedTracks.values())
                    .filter((item) => item.track)
                    .map((item) => ({ ...item.track, _scene: item.scene }))
                : playlistSource === "favorites"
                  ? Array.from(favoritedTracks.values())
                      .filter((item) => item.track)
                      .map((item) => ({ ...item.track, _scene: item.scene }))
                  : playlistSource === "playlist"
                    ? playlist
                    : playlistSource === "temp"
                      ? tempPlaylist
                      : playlistSource === "explore"
                        ? exploreTracks
                        : [];

            const sourceType = ["liked", "favorites", "playlist", "temp", "explore"].includes(playlistSource)
              ? playlistSource
              : "playlist";

            return (
              <PlaylistModal
                tracks={sourceTracks}
                currentTrackId={currentTrack?.id}
                sourceType={sourceType}
                playlists={playlists}
                onRemove={
                  playlistSource === "playlist"
                    ? (idx) => {
                        const t = playlist[idx];
                        if (t) removeFromPlaylist(t.id);
                      }
                    : playlistSource === "temp"
                      ? (idx) => {
                          const t = tempPlaylist[idx];
                          if (t) removeFromTempPlaylist(t.id);
                        }
                      : undefined
                }
                onClear={
                  playlistSource === "playlist"
                    ? clearPlaylist
                    : playlistSource === "temp"
                      ? clearTempPlaylist
                      : undefined
                }
                onPlay={(track) => {
                  const sc =
                    scenes.find(
                      (s) => s.id === track.scene_id || s.atmo === track.atmosphere
                    ) || currentScene;
                  playTrack(track, sc);
                  setShowPlaylist(false);
                }}
                onClose={() => setShowPlaylist(false)}
                lang={lang}
              />
            );
          })()}

        {/* User Library Modal */}
        {showUserLibrary && (
          <UserLibraryModal
            likedTracks={likedTracks}
            favoritedTracks={favoritedTracks}
            lang={lang}
            onPlayTrack={(track, scene) => {
              const sc = scenes.find((s) => s.id === scene || s.atmo === scene) || null;
              playTrack(track, sc);
              setShowUserLibrary(false);
            }}
            onToggleLike={(track, scene) =>
              onToggleLike(track, scene ? { atmo: scene } : null)
            }
            onToggleFavorite={(track, scene) =>
              onToggleFavorite(track, scene ? { atmo: scene } : null)
            }
            onClose={() => setShowUserLibrary(false)}
            initialTab={userLibraryTab}
            onCreatePlaylist={handleCreatePlaylist}
            onShowPricing={() => setShowPricingModal(true)}
          />
        )}

        {/* Pricing Modal */}
        {showPricingModal && (
          <PricingModal
            lang={lang}
            onClose={() => setShowPricingModal(false)}
          />
        )}

        {/* Login Modal */}
        {showLoginMethod && (
          <LoginMethodModal
            lang={lang}
            onClose={() => setShowLoginMethod(false)}
            onSelectEmail={() => {
              setShowLoginMethod(false);
              setShowEmailLogin(true);
            }}
            onGoogleSuccess={handleCredentialResponse}
          />
        )}

        {/* Guest Prompt Modal */}
        {guestPrompt && (
          <GuestPromptModal
            lang={lang}
            title={guestPrompt.title}
            description={guestPrompt.description}
            onSignIn={() => {
              setGuestPrompt(null);
              setShowLoginMethod(true);
            }}
            onClose={() => setGuestPrompt(null)}
          />
        )}

        {/* Email Login Modal */}
        {showEmailLogin && (
          <EmailLoginModal
            lang={lang}
            onClose={() => setShowEmailLogin(false)}
            onSuccess={handleEmailLoginSuccess}
          />
        )}

        {/* PlayerBar (bottom fixed bar) */}
        <PlayerBar
          currentTrack={currentTrack}
          currentScene={currentScene}
          isPlaying={isPlaying}
          audioRef={audioRef}
          likedTracks={likedTracks}
          favoritedTracks={favoritedTracks}
          playlist={playlist}
          playlistSource={playlistSource}
          playlistName={playlistName}
          sourceTrackCount={
            playlistSource === "playlist"
              ? playlist.length
              : playlistSource === "temp"
                ? tempPlaylist.length
                : playlistSource === "liked"
                  ? likedTracks.size
                  : playlistSource === "favorites"
                    ? favoritedTracks.size
                    : playlistSource === "explore"
                      ? exploreTracks.length
                      : 0
          }
          loopMode={loopMode}
          onChangeSource={setPlaylistSource}
          onChangeLoopMode={setLoopMode}
          onTogglePlay={togglePlay}
          onPrev={prevTrack}
          onNext={nextTrack}
          onToggleLike={() => onToggleLike(currentTrack, currentScene)}
          onToggleFavorite={() => onToggleFavorite(currentTrack, currentScene)}
          onDislike={handleDislike}
          onShare={handleShare}
          onOpenPlayer={() => setPlayerMode("fullscreen")}
          onOpenImmersive={() => setPlayerMode("immersive")}
          onTogglePlaylist={() => {
            setShowPlaylist(!showPlaylist);
          }}
          onToggleLang={toggleLang}
          lang={lang}
        />

        {/* Toast */}
        {toast && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[999] animate-fade-in pointer-events-none">
            <div
              className={`px-6 py-3 rounded-lg shadow-lg text-sm font-medium backdrop-blur-sm ${
                toast.type === "success"
                  ? "bg-green-600/90 text-white"
                  : toast.type === "error"
                    ? "bg-red-600/90 text-white"
                    : "bg-yellow-600/90 text-white"
              }`}
            >
              {toast.message}
            </div>
          </div>
        )}
      </div>

      {/* === Player Mode Panels === */}

      {/* Fullscreen Player (when playerMode === 'fullscreen') */}
      {playerMode === "fullscreen" && (
        <FullscreenPlayer
          likedTracks={likedTracks}
          favoritedTracks={favoritedTracks}
          onToggleLike={(id, track, scene) => onToggleLike(track, scene)}
          onToggleFavorite={(id, track, scene) => onToggleFavorite(track, scene)}
          onAddToPlaylist={addToTempPlaylist}
          onTagClick={handleTagClick}
          playlists={playlists}
          lang={lang}
        />
      )}

      {/* Immersive Player (when playerMode === 'immersive') */}
      {playerMode === "immersive" && (
        <ImmersivePlayer
          likedTracks={likedTracks}
          favoritedTracks={favoritedTracks}
          onToggleLike={(id, track, scene) => onToggleLike(track, scene)}
          onToggleFavorite={(id, track, scene) => onToggleFavorite(track, scene)}
          playlists={playlists}
        />
      )}
    </>
  );
}
